import { Image, Palette, RefreshCw, Save, Upload, WandSparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brandingAssetUrl, useBranding } from "@/hooks/useBranding";

const FALLBACKS = {
  logo_file_id: "/marsem-logo.webp",
  login_image_file_id: "/marsem-course-hero.png",
  student_dashboard_image_file_id: "/marsem-course-hero.png",
};

function BrandingUploadCard({ field, title, description, value, localPreview, onUpload, onReset, busy }) {
  const src = localPreview || brandingAssetUrl(value) || FALLBACKS[field];
  const isLogo = field === "logo_file_id";
  return (
    <article className={`branding-upload-card ${isLogo ? "logo-card" : field === "student_dashboard_image_file_id" ? "dashboard-art-card" : "login-art-card"}`}>
      <header>
        <span>{isLogo ? <WandSparkles /> : <Image />}</span>
        <div><h2>{title}</h2><p>{description}</p></div>
      </header>
      <div className="branding-preview">
        <img src={src} alt={title} />
        <label>
          <Upload /> {busy ? "PRZESYŁANIE…" : "WYBIERZ OBRAZ"}
          <input type="file" accept="image/png,image/jpeg,image/webp" disabled={busy} onChange={(event) => onUpload(field, event.target.files?.[0])} />
        </label>
      </div>
      <footer>
        <span>PNG, JPG lub WEBP · maks. 12 MB</span>
        <button type="button" onClick={() => onReset(field)} disabled={!value && !localPreview}><RefreshCw /> PRZYWRÓĆ DOMYŚLNE</button>
      </footer>
    </article>
  );
}

export default function BrandingPage() {
  const { data, isLoading } = useBranding();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState({ logo_file_id: null, login_image_file_id: null, student_dashboard_image_file_id: null });
  const [previews, setPreviews] = useState({});
  const [uploading, setUploading] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (data) setDraft({
      logo_file_id: data.logo_file_id || null,
      login_image_file_id: data.login_image_file_id || null,
      student_dashboard_image_file_id: data.student_dashboard_image_file_id || null,
    });
  }, [data]);

  const upload = async (field, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Wybierz plik graficzny");
    if (file.size > 12 * 1024 * 1024) return toast.error("Obraz może mieć maksymalnie 12 MB");
    setUploading(field);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data: uploaded } = await api.post("/files", form);
      const previewUrl = URL.createObjectURL(file);
      setDraft((current) => ({ ...current, [field]: uploaded.id }));
      setPreviews((current) => {
        if (current[field]) URL.revokeObjectURL(current[field]);
        return { ...current, [field]: previewUrl };
      });
      toast.success("Obraz przesłany. Zapisz zmiany, aby go opublikować.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nie udało się przesłać obrazu");
    } finally {
      setUploading("");
    }
  };

  const reset = (field) => {
    if (previews[field]) URL.revokeObjectURL(previews[field]);
    setPreviews((current) => ({ ...current, [field]: "" }));
    setDraft((current) => ({ ...current, [field]: null }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/admin/branding", draft);
      setDraft(saved);
      Object.values(previews).forEach((url) => url && URL.revokeObjectURL(url));
      setPreviews({});
      await queryClient.invalidateQueries({ queryKey: ["platform-branding"] });
      toast.success("Wygląd platformy został opublikowany");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Nie udało się zapisać wyglądu");
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <div className="page loading">Ładowanie ustawień wyglądu…</div>;

  return (
    <div className="page branding-page" data-testid="branding-page">
      <div className="page-heading">
        <div><span className="eyebrow">TOŻSAMOŚĆ PLATFORMY</span><h1>Wygląd MARSEM LAB</h1><p>Zmień logo, obraz logowania oraz główną grafikę widoczną na pulpicie ucznia.</p></div>
        <button className="gold-button" onClick={save} disabled={saving || Boolean(uploading)} data-testid="save-branding-button"><Save /> {saving ? "ZAPISYWANIE…" : "OPUBLIKUJ ZMIANY"}</button>
      </div>
      <div className="branding-intro"><Palette /><div><strong>Jedna marka, wszystkie ekrany</strong><span>Po zapisaniu nowe materiały pojawią się automatycznie w nawigacji, na stronie logowania i na pulpicie ucznia.</span></div></div>
      <div className="branding-grid">
        <BrandingUploadCard field="logo_file_id" title="Logo platformy" description="Najlepiej działa szeroki plik z przezroczystym tłem." value={draft.logo_file_id} localPreview={previews.logo_file_id} onUpload={upload} onReset={reset} busy={uploading === "logo_file_id"} />
        <BrandingUploadCard field="login_image_file_id" title="Obraz strony logowania" description="Kadr poziomy, który dobrze wygląda także na telefonie." value={draft.login_image_file_id} localPreview={previews.login_image_file_id} onUpload={upload} onReset={reset} busy={uploading === "login_image_file_id"} />
        <BrandingUploadCard field="student_dashboard_image_file_id" title="Obraz pulpitu ucznia" description="Główna grafika kursu na pulpicie studenta. Najlepiej sprawdzi się szeroki kadr z wolnym miejscem na tekst po prawej stronie." value={draft.student_dashboard_image_file_id} localPreview={previews.student_dashboard_image_file_id} onUpload={upload} onReset={reset} busy={uploading === "student_dashboard_image_file_id"} />
      </div>
    </div>
  );
}
